// src/registry/useClientComponent.js
import React from 'react';
import { useSelector } from 'react-redux';
import { useAbility } from '@casl/react';
import { Alert, AlertTitle, Paper, Typography } from '@mui/material';
import { componentRegistry } from './ComponentRegistry';
import {
  selectClient,
  selectViewRoleName,
} from '../managers/AuthManager/selectors';
import { AbilityContext } from '../casl/AbilityContext';

function NoAccessText() {
  return (
    <Paper elevation={2}>
      <Alert severity="error">
        <AlertTitle>Access denied</AlertTitle>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Alert>
    </Paper>
  );
}

/**
 * Hook to get a client-specific component implementation
 * @param {string} componentKey - Component identifier
 * @param {React.ComponentType} defaultComponent - Fallback component if not found in registry
 * @returns {React.ComponentType} - The appropriate component for the current client
 */
export function useClientComponent(componentKey, defaultComponent = null) {
  // Return the appropriate component from the registry
  return componentRegistry.get(componentKey) || defaultComponent;
}

export const useComponent = (domain, view, client, role, fallback = null) => {
  const variants = [
    `${domain}:${view}:${client}:${role}`,
    `${domain}:${view}:${client}:*`,
    `${domain}:${view}:*:${role}`,
    `${domain}:${view}:*:*`,
    `${domain}:${view}:default`,
  ];

  const component = variants
    .map(key => componentRegistry.get(key))
    .find(comp => !!comp);

  return component || fallback || (() => <div>Component not available</div>);
};

export const useProtectedComponent = ({
  subject,
  action = 'read',
  fallback,
}) => {
  const ability = useAbility(AbilityContext);
  const client = useSelector(selectClient);
  const role = useSelector(selectViewRoleName);
  const Component = useComponent(subject, client, role, fallback);
  if (!ability.can(action, subject)) return <NoAccessText />;

  return Component;
};
